import { Link, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useBookingModal } from "@/contexts/BookingModalContext";
import { useCopyOverride, COPY_DEFAULTS, isPromoActive, sanitizeColor } from "@/lib/copyOverrides";
import { sendPromoEvent, sendPromoViewOnce } from "@/lib/promoEvents";


const PromoBanner = () => {
  const [isHidden, setIsHidden] = useState(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isAnyModalOpen } = useBookingModal();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsHidden(true);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
        scrollTimeoutRef.current = setTimeout(() => {
          setIsHidden(false);
        }, 3000);
      } else {
        setIsHidden(false);
        if (scrollTimeoutRef.current) {
          clearTimeout(scrollTimeoutRef.current);
        }
      }
    };
    
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // Hide on spring break page, easter page, romantic getaway page, kayak city pages, family cabins city pages, or when modal is open
  const kayakCityRoutes = ["/kayaksanantonio", "/kayak-houston", "/kayak-austin", "/kayak-new-braunfels", "/kayak-texas-hill-country"];
  const familyCabinsRoutes = ["/family-cabins", "/family-cabins-san-antonio", "/family-cabins-houston", "/family-cabins-austin", "/family-cabins-new-braunfels", "/family-cabins-texas-hill-country"];
  const waterfrontCabinsRoutes = ["/waterfront-cabins", "/waterfront-cabins-san-antonio", "/waterfront-cabins-austin", "/waterfront-cabins-houston", "/waterfront-cabins-new-braunfels"];
  const hideBanner =
    location.pathname === "/book" ||
    location.pathname.startsWith("/book-now") ||
    location.pathname.startsWith("/book5") ||
    location.pathname.startsWith("/images5") ||
    location.pathname.startsWith("/map5") ||
    location.pathname === "/springbreak" ||
    location.pathname === "/easter" ||
    location.pathname === "/romanticgetaway" ||
    location.pathname.startsWith("/admin") ||
    kayakCityRoutes.includes(location.pathname) ||
    familyCabinsRoutes.includes(location.pathname) ||
    waterfrontCabinsRoutes.includes(location.pathname) ||
    isAnyModalOpen;

  const desktopCopy = useCopyOverride("banner.desktop", COPY_DEFAULTS["banner.desktop"]);
  const mobileCopy = useCopyOverride("banner.mobile", COPY_DEFAULTS["banner.mobile"]);
  const href = useCopyOverride("banner.href", COPY_DEFAULTS["banner.href"]);
  const bg = sanitizeColor(useCopyOverride("banner.bg", COPY_DEFAULTS["banner.bg"]));
  const fg = sanitizeColor(useCopyOverride("banner.fg", COPY_DEFAULTS["banner.fg"]));
  const promoActive = isPromoActive("banner");

  const visible = !hideBanner && promoActive;
  useEffect(() => {
    if (visible) sendPromoViewOnce("banner", location.pathname);
  }, [visible, location.pathname]);

  if (!visible) return null;

  const isExternal = /^https?:\/\//i.test(href);
  const className = `fixed top-0 left-0 right-0 z-[60] block w-full text-center transition-all duration-300 ${
    bg ? "" : "bg-white hover:bg-gray-50"
  } ${
    isHidden ? "py-0 opacity-0 pointer-events-none -translate-y-full" : "py-3 sm:py-3 opacity-100 translate-y-0"
  }`;
  const barStyle = bg || fg ? { backgroundColor: bg, color: fg } : undefined;
  const textStyle = fg ? { color: fg } : undefined;
  const handleClick = () => sendPromoEvent("banner", "click");

  const inner = (
    <>
      {/* Desktop */}
      <span
        className={`hidden sm:flex items-center justify-center gap-2 font-semibold px-2 leading-tight text-base whitespace-pre-wrap ${fg ? "" : "text-[#005283]"}`}
        style={textStyle}
      >
        {desktopCopy}
      </span>
      {/* Mobile */}
      <span
        className={`flex sm:hidden items-center justify-center gap-1 font-semibold px-2 leading-tight text-[11px] whitespace-pre-wrap ${fg ? "" : "text-[#005283]"}`}
        style={textStyle}
      >
        {mobileCopy}
      </span>
    </>
  );

  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} style={barStyle} onClick={handleClick}>
        {inner}
      </a>
    );
  }

  return (
    <Link to={href} className={className} style={barStyle} onClick={handleClick}>
      {inner}
    </Link>
  );
};


export default PromoBanner;
